import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: "default" | "info" | "success" | "warning";
  }
>(({ className, sideOffset = 6, variant = "default", children, ...props }, ref) => {
  const variantStyles = {
    default: "bg-popover text-popover-foreground border-border",
    info: "bg-primary text-primary-foreground border-primary/20",
    success: "bg-green-600 text-white border-green-500/20",
    warning: "bg-amber-500 text-white border-amber-400/20",
  };

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      {...props}
      className={cn(
        "z-50 overflow-hidden rounded-lg border px-3 py-2 text-sm shadow-lg",
        "backdrop-blur-sm",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        variantStyles[variant],
        className,
      )}
    >
      {children}
    </TooltipPrimitive.Content>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Enhanced tooltip with icon support
interface AnimatedTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  variant?: "default" | "info" | "success" | "warning";
  side?: "top" | "right" | "bottom" | "left";
  delayDuration?: number;
}

const AnimatedTooltip: React.FC<AnimatedTooltipProps> = ({
  content,
  children,
  variant = "default",
  side = "top",
  delayDuration = 200,
}) => {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help hover:scale-105 transition-transform">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent variant={variant} side={side}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, AnimatedTooltip };
